import { create } from "zustand";

export interface PreferenceBasic {
	propertyType: string;
}

export interface PreferenceData {
	basic: PreferenceBasic;
}

export interface UserPreference {
	id: string;
	title: string;
	preference: PreferenceData;
}

interface UserPreferencesState {
	preferences: UserPreference[];
	loading: boolean;
	error: string | null;

	// Selected Group State (synced with IndexedDB)
	selectedGroupId: string | undefined;
	relationship: "buyer" | "seller" | undefined;

	// Actions
	setPreferences: (preferences: UserPreference[]) => void;
	setLoading: (loading: boolean) => void;
	setError: (error: string | null) => void;
	addPreference: (preference: UserPreference) => void;
	removePreference: (preferenceId: string) => void;
	updatePreference: (preferenceId: string, updatedPreference: Partial<UserPreference>) => void;

	// Selected Group Actions
	setSelectedGroup: (params: { selectedGroupId?: string; relationship?: "buyer" | "seller" }) => void;
	clearSelectedGroup: () => void;

	// Getters
	getPreferenceById: (preferenceId: string) => UserPreference | undefined;
	hasPreferences: () => boolean;

	// Utilities
	clearAll: () => void;
	refetchPreferences: () => void;
}

export const useUserPreferencesStore = create<UserPreferencesState>((set, get) => ({
	preferences: [],
	loading: false,
	error: null,

	// Selected Group State (initialized from IndexedDB via sync hook)
	selectedGroupId: undefined,
	relationship: undefined,

	setPreferences: (preferences: UserPreference[]) => {
		set({ preferences, error: null });
	},

	setLoading: (loading: boolean) => {
		set({ loading });
	},

	setError: (error: string | null) => {
		set({ error, loading: false });
	},

	addPreference: (preference: UserPreference) => {
		set(state => ({
			preferences: [...state.preferences, preference],
		}));
	},

	removePreference: (preferenceId: string) => {
		set(state => ({
			preferences: state.preferences.filter(pref => pref.id !== preferenceId),
		}));
	},

	updatePreference: (preferenceId: string, updatedPreference: Partial<UserPreference>) => {
		set(state => ({
			preferences: state.preferences.map(pref => (pref.id === preferenceId ? { ...pref, ...updatedPreference } : pref)),
		}));
	},

	// Selected Group Actions
	setSelectedGroup: ({ selectedGroupId, relationship }) => {
		set({
			selectedGroupId: selectedGroupId,
			relationship: relationship,
		});
	},

	clearSelectedGroup: () => {
		set({
			selectedGroupId: undefined,
			relationship: undefined,
		});
	},

	getPreferenceById: (preferenceId: string) => {
		const state = get();
		return state.preferences.find(pref => pref.id === preferenceId);
	},

	hasPreferences: () => {
		const state = get();
		return state.preferences.length > 0;
	},

	clearAll: () => {
		set({
			preferences: [],
			loading: false,
			error: null,
		});
	},

	refetchPreferences: () => {
		// This will be used to trigger a refetch in components that use this store
		// The actual refetch logic will be handled by the useUserPreferences hook
		set(state => ({ ...state }));
	},
}));
